/**
 * SOFSEK — browser-side hydration hook for hash-mode deployments (D6/D8, §14).
 *
 * In a hash-mode deployment the stored value of an entity token or tracking
 * reference IS the hash — there are no parallel plaintext/hash columns. When
 * the public system parameter SFK.TRANSLATOR.URL is set, this hook batches the
 * displayed hash tokens to the customer's own Local Translator Agent (running
 * inside their firewall) and swaps in the decrypted text for display only; the
 * stored data is never touched. Unset (default) = plaintext deployment = the
 * hook is a no-op and values render exactly as stored.
 */
import { useEffect, useState } from 'react';
import { apiDb } from './api_client';

let cachedTranslatorUrl: string | null | undefined;

async function getTranslatorUrl(): Promise<string | null> {
  if (cachedTranslatorUrl !== undefined) return cachedTranslatorUrl;
  const { data } = await apiDb.from('csm_system_parameters')
    .select('parameter_value').eq('internal_code', 'SFK.TRANSLATOR.URL').eq('is_active', 'Y');
  const value = (data ?? [])[0]?.parameter_value as string | undefined;
  cachedTranslatorUrl = value && value.length > 0 ? value : null;
  return cachedTranslatorUrl;
}

/** Best-effort display hydration for a set of tokens. No-op (identity map) when unconfigured. */
export function useTranslatorHydration(tokens: string[]): Record<string, string> {
  const [hydrated, setHydrated] = useState<Record<string, string>>({});
  const key = tokens.join(' ');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const url = await getTranslatorUrl();
      if (!url || tokens.length === 0) { if (!cancelled) setHydrated({}); return; }
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tokens }),
        });
        if (!res.ok) return;
        const body = (await res.json()) as { tokens?: Record<string, string> };
        if (!cancelled && body.tokens) setHydrated(body.tokens);
      } catch {
        // Translator unreachable — fall back silently to the stored (hashed) value.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return hydrated;
}

/** Resolve a single token through the hydration map, falling back to the stored value. */
export function resolveNameForToken(map: Record<string, string>, token: string): string {
  return map[token] ?? token;
}
